import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Search, User as UserIcon, Building2, Check } from 'lucide-react';
import { api } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import PhoneInput from './PhoneInput';
import DocumentInput, { formatDocBR } from './DocumentInput';
import { formatPhoneBR } from './PhoneInput';

/**
 * Standalone dialog to search & select a person, with inline "new" support.
 * Use this when you want to trigger the picker from any button (e.g., card header).
 */
export default function PersonPickerDialog({ open, onOpenChange, onSelect, selectedId }) {
  const [search, setSearch] = useState('');
  const [people, setPeople] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [newForm, setNewForm] = useState({ type: 'PF', name: '', document: '', phone: '', email: '', address: '', notes: '' });

  useEffect(() => {
    if (!open) return;
    setLoadingList(true);
    const params = {};
    if (search) params.search = search;
    api.get('/people', { params })
      .then(({ data }) => setPeople(data))
      .finally(() => setLoadingList(false));
  }, [open, search]);

  const handleSelect = (p) => {
    onOpenChange(false);
    if (onSelect) onSelect(p);
  };

  const saveNew = async () => {
    if (!newForm.name.trim()) { toast.error('Nome é obrigatório'); return; }
    try {
      const payload = {
        type: newForm.type,
        name: newForm.name.trim(),
        document: newForm.document || null,
        phone: newForm.phone || null,
        email: newForm.email.trim() || null,
        address: newForm.address.trim() || null,
        notes: newForm.notes.trim() || null,
      };
      const { data } = await api.post('/people', payload);
      toast.success('Pessoa cadastrada');
      setShowNew(false);
      setNewForm({ type: 'PF', name: '', document: '', phone: '', email: '', address: '', notes: '' });
      handleSelect(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao cadastrar');
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Selecionar pessoa</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nome, CPF/CNPJ, telefone..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                autoFocus
              />
            </div>
            <div className="max-h-[50vh] overflow-y-auto space-y-1 pr-1">
              {loadingList && <div className="text-sm text-muted-foreground py-6 text-center">Carregando...</div>}
              {!loadingList && people.length === 0 && (
                <div className="text-sm text-muted-foreground py-6 text-center">
                  Nenhuma pessoa encontrada. Clique em "Cadastrar nova" abaixo.
                </div>
              )}
              {!loadingList && people.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => handleSelect(p)}
                  className={`w-full text-left p-3 rounded-lg border border-border/70 hover:bg-muted/40 transition flex items-start gap-3 ${selectedId === p.id ? 'bg-muted/60 border-[hsl(var(--primary))]' : ''}`}
                  data-testid="person-picker-option"
                >
                  <div className="h-8 w-8 rounded bg-muted grid place-items-center text-muted-foreground shrink-0">
                    {p.type === 'PJ' ? <Building2 className="h-4 w-4" /> : <UserIcon className="h-4 w-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{p.name}</div>
                    <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] uppercase">{p.type}</span>
                      {p.document && <span className="tabular">{formatDocBR(p.document)}</span>}
                      {p.phone && <span className="tabular">· {formatPhoneBR(p.phone)}</span>}
                    </div>
                  </div>
                  {selectedId === p.id && <Check className="h-4 w-4 text-[hsl(var(--primary))] shrink-0" />}
                </button>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Fechar</Button>
            <Button onClick={() => { onOpenChange(false); setShowNew(true); }} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90">
              <Plus className="h-4 w-4 mr-1.5" /> Cadastrar nova
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nova pessoa</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-[140px_1fr] gap-3">
              <div>
                <Label>Tipo *</Label>
                <Select value={newForm.type} onValueChange={(v) => setNewForm((f) => ({ ...f, type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PF">PF</SelectItem>
                    <SelectItem value="PJ">PJ</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{newForm.type === 'PJ' ? 'Razão social' : 'Nome'} *</Label>
                <Input value={newForm.name} onChange={(e) => setNewForm((f) => ({ ...f, name: e.target.value }))} data-testid="picker-new-name" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{newForm.type === 'PJ' ? 'CNPJ' : 'CPF'}</Label>
                <DocumentInput value={newForm.document} onChange={(v) => setNewForm((f) => ({ ...f, document: v }))} />
              </div>
              <div>
                <Label>Telefone</Label>
                <PhoneInput value={newForm.phone} onChange={(v) => setNewForm((f) => ({ ...f, phone: v }))} />
              </div>
            </div>
            <div>
              <Label>E-mail</Label>
              <Input type="email" value={newForm.email} onChange={(e) => setNewForm((f) => ({ ...f, email: e.target.value }))} />
            </div>
            <div>
              <Label>Endereço</Label>
              <Input value={newForm.address} onChange={(e) => setNewForm((f) => ({ ...f, address: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNew(false)}>Cancelar</Button>
            <Button onClick={saveNew} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90" data-testid="picker-new-save">
              Cadastrar e selecionar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
